import React, { useState, useMemo } from 'react';

function formatTime(totalSeconds) {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function roundTwo(n) {
  return Math.round(toNumber(n) * 100) / 100;
}

/**
 * Modal shown before trip completion for metered trips.
 *
 * Sections:
 *  - Trip stats (distance, elapsed time, wait time)
 *  - Fare breakdown (flag, distance, time, wait)
 *  - Extras: each `pricing[]` row with type === 'each' becomes a +/- qty stepper
 *  - Tip / Toll / Surcharge: editable dollar inputs defaulting to `disposition.price.*`
 *  - Total: auto-computed as `max(min, flag + dist + meterTime + wait + extras + tip + toll + surcharge)`
 *    and free-editable with a "Reset to calculated" button.
 *
 * onConfirm is called with `(totalDollars, dispositionUpdates, extras)` where
 *   dispositionUpdates = { tip, toll, surcharge }
 *   extras = [{ id, name, rate, qty }, ...]  // only entries with qty > 0
 */
const FareConfirmationModal = ({
  isOpen,
  fareData,
  extras: extrasProp = [],
  dispositionPrice = null,
  onConfirm,
  onCancel,
  /** Primary button text. Payment-first flows confirm the fare, then collect. */
  confirmLabel = 'Confirm & Complete',
}) => {
  // Per-extra qty map keyed by extra id (or index fallback).
  const [extraQty, setExtraQty] = useState(() => {
    const m = {};
    (extrasProp || []).forEach((x, i) => {
      const k = String(x.id || `idx_${i}`);
      m[k] = Number(x.qty) > 0 ? Number(x.qty) : 0;
    });
    return m;
  });

  const [tip, setTip] = useState(() => roundTwo(dispositionPrice?.tip));
  const [toll, setToll] = useState(() => roundTwo(dispositionPrice?.toll));
  const [surcharge, setSurcharge] = useState(() => roundTwo(dispositionPrice?.surcharge));

  // Free-edit total override; null means "use calculated".
  const [adjustedTotal, setAdjustedTotal] = useState('');

  const extrasArr = useMemo(() => Array.isArray(extrasProp) ? extrasProp : [], [extrasProp]);

  const extrasTotal = useMemo(() => {
    let s = 0;
    extrasArr.forEach((x, i) => {
      const k = String(x.id || `idx_${i}`);
      const q = Number(extraQty[k]) || 0;
      const r = toNumber(x.rate);
      if (q > 0 && r > 0) s += q * r;
    });
    return roundTwo(s);
  }, [extrasArr, extraQty]);

  const calculatedTotal = useMemo(() => {
    if (!fareData) return 0;
    const baseSum =
      toNumber(fareData.flagRate) +
      toNumber(fareData.distanceCharge) +
      toNumber(fareData.timeCharge) +
      toNumber(fareData.waitCharge) +
      extrasTotal +
      toNumber(tip) +
      toNumber(toll) +
      toNumber(surcharge);
    const min = toNumber(fareData.minimumFare);
    return roundTwo(min > 0 ? Math.max(baseSum, min) : baseSum);
  }, [fareData, extrasTotal, tip, toll, surcharge]);

  const totalIsOverridden = adjustedTotal !== '' && Number(adjustedTotal) !== calculatedTotal;
  const totalToShow = adjustedTotal !== '' ? adjustedTotal : calculatedTotal.toFixed(2);

  if (!isOpen || !fareData) return null;

  const handleConfirm = () => {
    const finalTotal =
      adjustedTotal !== '' && Number.isFinite(parseFloat(adjustedTotal))
        ? parseFloat(adjustedTotal)
        : calculatedTotal;
    const usedExtras = extrasArr
      .map((x, i) => {
        const k = String(x.id || `idx_${i}`);
        const q = Number(extraQty[k]) || 0;
        return { id: x.id || '', name: x.name || '', rate: toNumber(x.rate), qty: q };
      })
      .filter((x) => x.qty > 0);
    const dispositionUpdates = {
      tip: roundTwo(tip),
      toll: roundTwo(toll),
      surcharge: roundTwo(surcharge),
    };
    onConfirm(finalTotal, dispositionUpdates, usedExtras);
  };

  const showWaitTime = (fareData.waitSeconds || 0) > 0;
  const showWaitChargeRow = showWaitTime || fareData.waitCharge > 0;
  const minFare = toNumber(fareData.minimumFare);
  const showMinNote = minFare > 0 && calculatedTotal <= minFare + 0.0001;

  const incExtra = (key) =>
    setExtraQty((prev) => ({ ...prev, [key]: (Number(prev[key]) || 0) + 1 }));
  const decExtra = (key) =>
    setExtraQty((prev) => ({ ...prev, [key]: Math.max(0, (Number(prev[key]) || 0) - 1) }));

  const moneyInput = (val, setter) => (
    <input
      type="number"
      step="0.01"
      min="0"
      value={val}
      onChange={(e) => setter(e.target.value)}
      className="w-20 text-right text-sm font-mono border border-gray-300 rounded px-2 py-1"
    />
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[2200]">
      <div className="bg-white rounded-lg max-w-sm w-full mx-4 shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="bg-gray-900 text-white px-5 py-3">
          <h3 className="text-lg font-bold">Fare Summary</h3>
        </div>

        <div className="px-5 py-4 space-y-3 overflow-y-auto">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Distance</span>
            <span className="font-mono">{Number(fareData.distance || 0).toFixed(1)} mi</span>
          </div>
          <div className="flex justify-between text-sm text-gray-600">
            <span>Elapsed Time</span>
            <span className="font-mono">{formatTime(fareData.elapsedSeconds || 0)}</span>
          </div>
          {showWaitTime && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>Wait Time</span>
              <span className="font-mono">{formatTime(fareData.waitSeconds || 0)}</span>
            </div>
          )}

          <hr className="border-gray-200" />

          <div className="flex justify-between text-sm">
            <span>Flag Drop</span>
            <span className="font-mono">${toNumber(fareData.flagRate).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Distance Charge</span>
            <span className="font-mono">${toNumber(fareData.distanceCharge).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Time Charge</span>
            <span className="font-mono">${toNumber(fareData.timeCharge).toFixed(2)}</span>
          </div>
          {showWaitChargeRow && (
            <div className="flex justify-between text-sm">
              <span>Wait Charge</span>
              <span className="font-mono">${toNumber(fareData.waitCharge).toFixed(2)}</span>
            </div>
          )}

          {extrasArr.length > 0 && (
            <>
              <hr className="border-gray-200" />
              <div className="text-sm font-semibold text-gray-800">Extras</div>
              {extrasArr.map((x, i) => {
                const k = String(x.id || `idx_${i}`);
                const q = Number(extraQty[k]) || 0;
                const rate = toNumber(x.rate);
                const sub = roundTwo(q * rate);
                return (
                  <div key={k} className="flex justify-between items-center text-sm">
                    <div className="flex flex-col flex-1 mr-2">
                      <span>{x.name || 'Extra'}</span>
                      <span className="text-xs text-gray-500 font-mono">${rate.toFixed(2)} each</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="w-7 h-7 rounded bg-gray-200 hover:bg-gray-300 font-bold"
                        onClick={() => decExtra(k)}
                        disabled={q <= 0}
                      >
                        −
                      </button>
                      <span className="w-6 text-center font-mono">{q}</span>
                      <button
                        type="button"
                        className="w-7 h-7 rounded bg-gray-200 hover:bg-gray-300 font-bold"
                        onClick={() => incExtra(k)}
                      >
                        +
                      </button>
                      <span className="w-16 text-right font-mono">${sub.toFixed(2)}</span>
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">Extras Total</span>
                <span className="font-mono">${extrasTotal.toFixed(2)}</span>
              </div>
            </>
          )}

          <hr className="border-gray-200" />
          {/* Tip / Toll / Surcharge section temporarily hidden.
          <div className="text-sm font-semibold text-gray-800">Tip / Toll / Surcharge</div>
          <div className="flex justify-between items-center text-sm">
            <span>Tip</span>
            {moneyInput(tip, setTip)}
          </div>
          <div className="flex justify-between items-center text-sm">
            <span>Toll</span>
            {moneyInput(toll, setToll)}
          </div>
          <div className="flex justify-between items-center text-sm">
            <span>Surcharge</span>
            {moneyInput(surcharge, setSurcharge)}
          </div>
          */}

          {showMinNote && (
            <div className="text-xs text-gray-500 italic">
              Minimum fare of ${minFare.toFixed(2)} applied
            </div>
          )}

          <hr className="border-gray-200" />

          <div className="flex justify-between items-center">
            <span className="font-bold text-lg">Total</span>
            <div className="flex items-center">
              <span className="text-lg mr-1">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={totalToShow}
                onChange={(e) => setAdjustedTotal(e.target.value)}
                className="w-24 text-right text-lg font-bold font-mono border border-gray-300 rounded px-2 py-1"
              />
            </div>
          </div>
          {totalIsOverridden && (
            <button
              onClick={() => setAdjustedTotal('')}
              className="text-xs text-blue-600 underline"
            >
              Reset to calculated: ${calculatedTotal.toFixed(2)}
            </button>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 bg-gray-50">
          <button
            onClick={onCancel}
            className="flex-1 bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 bg-green-600 text-white font-bold py-2 px-4 rounded hover:bg-green-700"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FareConfirmationModal;
